import { observable, autoinject, bindable } from "aurelia-framework";
import { AccountListService, AccountGroupStructure, AccountStructure, IAccountGroupStructureAM } from "../../accounts/account-api";
import { AccountTagService, IAccountTagAM } from "../../accounts/account-tag-api";
import { AccountingDimensionService, IAccountingDimensionAM } from "../../accounting-dimensions/accounting-dimension-api";
import { MeasureService, IMeasureSummaryAM } from "../../measures/services/measure-service";
import { includes, keys } from "lodash";
import { SideBar } from "../../../components/side-bar/side-bar";
import { IReportSource, IReportFilterCollection } from "../models/report-template";

@autoinject
export class ReportFilters {
	@bindable sections: string;
	@bindable customerId: string;
	@bindable templateSource: IReportSource;
	@bindable change: (source: any) => void;
	@bindable measureCodes: string[];

	accountGroups: AccountGroupStructure[];
	accountTags: IAccountTagAM[];
	accountingDimensions: IAccountingDimensionAM[];
	measures: IMeasureSummaryAM[];

	selectedTags: { [id: string]: boolean } = {};
	forbiddenTags: { [id: string]: boolean } = {};
	selectedAccountingDimensionValues: { [id: string]: boolean } = {};
	selectedMeasures: { [id: string]: boolean } = {};
	forbiddenMeasures: { [id: string]: boolean } = {};

	private ensureTemplateSourceExistence() {
		if (!this.templateSource) {
			this.templateSource = { filters: {} };
		}
		else if (!this.templateSource.filters) {
			this.templateSource.filters = {};
		}
	}

	updateAccountSelection(selectionModel: IAccountSelectionModel) {
		this.templateSource.filters.accounts = selectionModel.accounts;
		this.templateSource.filters.accountGroups = selectionModel.accountGroups;
		this.templateSource.filters.accountClasses = selectionModel.accountClasses;
		const source = this.templateSource;
		
		this.change({source});
	}

	onTagsChanged() {
		
		const wantedTags = keys(this.selectedTags).filter(tagId => this.selectedTags[tagId]);
		
		// Remove the selected tags from forbidden tags
		wantedTags.forEach(tagId => this.forbiddenTags[tagId] = false);
		
		this.templateSource.filters.accountTags = wantedTags;
		this.templateSource.filters.forbiddenAccountTags = keys(this.forbiddenTags).filter(tagId => this.forbiddenTags[tagId]);

		const source = this.templateSource;
		this.change({source});
	}

	onForbiddenTagsChanged() {

		const unwantedTags = keys(this.forbiddenTags).filter(tagId => this.forbiddenTags[tagId]);

		// Remove the forbidden tags from selected tags
		unwantedTags.forEach(tagId => this.selectedTags[tagId] = false);

		this.templateSource.filters.accountTags = keys(this.selectedTags).filter(tagId => this.selectedTags[tagId]);
		this.templateSource.filters.forbiddenAccountTags = unwantedTags;

		const source = this.templateSource;
		this.change({source});
	}

	onAccountingDimensionValuesChanged() {
		this.templateSource.filters.dimensionValues = keys(this.selectedAccountingDimensionValues).filter(valueId => this.selectedAccountingDimensionValues[valueId]);
		
		const source = this.templateSource;
		this.change({source});
	}

	onMeasuresChanged() {
		const wantedMeasures = keys(this.selectedMeasures).filter(id => this.selectedMeasures[id]);

		// Remove selected measures from forbidden
		wantedMeasures.forEach(id => this.forbiddenMeasures[id] = false);

		this.templateSource.filters.measures = wantedMeasures;
		this.templateSource.filters.forbiddenMeasures = keys(this.forbiddenMeasures).filter(id => this.forbiddenMeasures[id]);

		const source = this.templateSource;
		this.change({source});
	}

	onForbiddenMeasuresChanged() {
		const unwantedMeasures = keys(this.forbiddenMeasures).filter(id => this.forbiddenMeasures[id]);

		// Remove forbidden measures from selected
		unwantedMeasures.forEach(id => this.selectedMeasures[id] = false);

		this.templateSource.filters.measures = keys(this.selectedMeasures).filter(id => this.selectedMeasures[id]);
		this.templateSource.filters.forbiddenMeasures = unwantedMeasures;

		const source = this.templateSource;
		this.change({source});
	}

	constructor(private accountsApi: AccountListService, 
		private tagApi: AccountTagService, 
		private accountingDimensionApi: AccountingDimensionService,
		private measureService: MeasureService,
		private sideBar: SideBar) {	}

	async initialize() {
		const filters = this.sections.split(',');
		if(includes(filters, 'accountGroups')) {
			this.accountGroups = (await this.accountsApi.loadAccountGroups(this.customerId)).map(AccountGroupStructure.accountStructureFromAM);
		}
		if(includes(filters, 'accountTags')) {
			this.accountTags = await this.tagApi.getAccountTags();
			this.accountTags.forEach(tag => this.selectedTags[tag.id] = (this.templateSource.filters.accountTags || []).indexOf(tag.id) >= 0);
			this.accountTags.forEach(tag => this.forbiddenTags[tag.id] = (this.templateSource.filters.forbiddenAccountTags || []).indexOf(tag.id) >= 0);
		}
		if(includes(filters, 'accountingDimensions')) {
			this.accountingDimensions = await this.accountingDimensionApi.getDimensionsForCustomer(this.customerId, false);
			this.accountingDimensions.forEach(dimension => 
				dimension.values.forEach(dimensionValue => 
					this.selectedAccountingDimensionValues[dimensionValue.id] = (this.templateSource.filters.dimensionValues || []).indexOf(dimensionValue.id) >= 0));
		}
		if(includes(filters, 'measures') && this.measureCodes && this.measureCodes.length) {
			const allMeasures = await this.measureService.getMeasuresForFilters();
			const codesSet = new Set(this.measureCodes);
			const forbiddenIds = new Set(this.templateSource.filters.forbiddenMeasures || []);
			this.measures = allMeasures.filter(m => codesSet.has(m.code) || forbiddenIds.has(m.id));
			this.measures.forEach(m => this.selectedMeasures[m.id] = (this.templateSource.filters.measures || []).indexOf(m.id) >= 0);
			this.measures.forEach(m => this.forbiddenMeasures[m.id] = (this.templateSource.filters.forbiddenMeasures || []).indexOf(m.id) >= 0);
		}
	}

	async bind() {
		this.ensureTemplateSourceExistence();
		await this.initialize();
	}
}

export interface IAccountSelectionModel {
	accounts: string[];
	accountGroups: string[];
	accountClasses: string[];
}
